// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@bg/App/Components/Notify";
import convertToCustomQuote from "@bg/App/Handlers/RequestQuote/ConvertToCustomQuote";
import convertToProjectList from "@bg/App/Handlers/RequestQuote/ConvertToProjectList";
import { linkIconStyleGenerator, pageTitlesPrintStyle } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { getCheckoutUrl } from "@bg/App/Store/Selectors/Checkout/CheckoutSelectors";
import Modal from "@bg/MobiusOverrides/Modal/Modal";
import { parseBoolean } from "@bg/XDL/Utilities";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import changeCustomerContext from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getLocationPathAndQuery } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import acceptJobQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/AcceptJobQuote";
import declineQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/DeclineQuote";
import deleteQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/DeleteQuote";
import submitQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SubmitQuote";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    oldQuoteState: state.xQuoteDetails.quote,
    rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
    rfqJobQuotesPageUrl: getPageLinkByPageType(state, "RfqJobQuotesPage")?.url,
    cartState: getCurrentCartState(state),
    onePageCheckout: getSettingsCollection(state).cartSettings.onePageCheckout,
    onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
    session: getSession(state),
    currentLocation: getLocationPathAndQuery(state),
    isQuoteDisabled: state.commonActions.isQuoteDisabled,
    changeCustomerPageUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
    isLoading: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId)?.isLoading,
    checkoutUrl: getCheckoutUrl(state, "shipping"),
});

const mapDispatchToProps = {
    deleteQuote,
    submitQuote,
    declineQuote,
    acceptJobQuote,
    convertToCustomQuote,
    convertToProjectList,
    changeCustomerContext,
};

type Props = WidgetProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsHeaderStyles {
    headerGridContainer?: InjectableCss;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: InjectableCss;
    deleteQuoteButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    declineQuoteButton?: ButtonPresentationProps;
    createThirdPartyQuote?: ButtonPresentationProps;
    acceptQuoteButton?: ButtonPresentationProps;
    buttonGridItem?: InjectableCss;
    printClickable?: LinkPresentationProps;
    jumpToMessages?: LinkPresentationProps;
    deleteQuoteClickable?: ClickableProps;
    submitQuoteClickable?: ClickableProps;
    declineQuoteClickable?: ClickableProps;
    acceptQuoteClickable?: ClickableProps;
    deleteQuoteModal?: TwoButtonModalStyles;
    cartNotificationModal?: TwoButtonModalStyles;
    badgeWrapper?: InjectableCss;
    requestedBadge?: TypographyPresentationProps;
    formField?: TextFieldPresentationProps;
    modalButtonsWrapper?: InjectableCss;
}

export const rfqQuoteDetailsHeaderStyles: RfqQuoteDetailsHeaderStyles = {
    headerGridContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            flex-wrap: wrap;
            @media (max-width: 450px) {
                flex-wrap: wrap;
            }
        `,
    },
    badgeWrapper: {
        css: css`
            flex-grow: 1;
            display: flex;
            align-items: flex-end;
            height: 40px;
        `,
    },
    printClickable: linkIconStyleGenerator({
        icon: "Print",
        textSize: "13px",
        iconSize: 14,
    }),
    jumpToMessages: linkIconStyleGenerator({
        icon: "ArrowDown",
        textSize: "13px",
        iconSize: 10,
        wrapperCss: css`
            margin-top: 8px;
            width: fit-content;
        `,
    }),
    deleteQuoteButton: {
        variant: "secondary",
        css: css`
            margin-left: 10px;
        `,
    },
    submitQuoteButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    declineQuoteButton: {
        variant: "secondary",
        css: css`
            margin-left: 10px;
        `,
    },
    createThirdPartyQuote: {
        variant: "secondary",
        css: css`
            margin-left: 10px;
        `,
    },
    acceptQuoteButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
    },
    titleGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    title: {
        variant: "h1",
        css: css`
            ${pageTitlesPrintStyle()}
        `,
    },
    buttonsHiddenContainer: {
        below: "lg",
        css: css`
            display: flex;
        `,
    },
    menuHiddenContainer: {
        above: "md",
    },
    narrowOverflowMenu: {
        cssOverrides: {
            menu: css`
                min-width: 100px;
                width: auto;
            `,
        },
    },
    requestedBadge: {
        size: "13px",
        lineHeight: "20px",
        css: css`
            border: 1px solid;
            padding: 0 8px;
            border-radius: 10px;
            display: inline-block;
            margin-left: 15px;
            text-transform: uppercase;
            @media (max-width: 650px) {
                margin: 0;
            }
            @media print {
                margin-left: 15px;
            }
        `,
    },
    modalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: 10px;
        `,
    },
    formField: {
        cssOverrides: {
            formField: css`
                margin: 20px 0;
            `,
        },
    },
};

const styles = rfqQuoteDetailsHeaderStyles;

const RfqQuoteDetailsHeader: FC<Props> = ({
    quoteState,
    history,
    rfqMyQuotesPageUrl,
    checkoutUrl,
    rfqJobQuotesPageUrl,
    cartState,
    onePageCheckout,
    onePageCheckoutPageUrl,
    deleteQuote,
    submitQuote,
    declineQuote,
    acceptJobQuote,
    convertToCustomQuote,
    convertToProjectList,
    changeCustomerContext,
    currentLocation,
    session,
    isQuoteDisabled,
    changeCustomerPageUrl,
    isLoading,
    oldQuoteState,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [deleteQuoteModalIsOpen, setDeleteQuoteModalIsOpen] = useState(false);
    const [cartNotificationModalIsOpen, setCartNotificationModalIsOpen] = useState(false);
    const [declineModalIsOpen, setDeclineModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectReasonError, setRejectReasonError] = useState("");

    const dispatch = useAppDispatch();
    useEffect(() => {
        if (quoteState.value?.properties.isExpired && !parseBoolean(quoteState.value?.properties.isExpired)) {
            dispatch({
                type: "commonActionSetter/SetAction",
                payload: {
                    name: "breadcrumbActions",
                    value: (
                        <Link {...styles.printClickable} onClick={openPrintDialog}>
                            {translate("Print")}
                        </Link>
                    ),
                },
            });
        }
        return () => {
            dispatch({
                type: "commonActionSetter/SetAction",
                payload: {
                    name: "breadcrumbActions",
                    value: null,
                },
            });
            dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: false } });
        };
    }, []);

    const quote = useMemo(() => {
        return quoteState.value || oldQuoteState;
    }, [quoteState.value, oldQuoteState]);

    useEffect(() => {
        if (quoteState.value && !parseBoolean(quoteState.value?.properties.isExpired)) {
            dispatch({
                type: "commonActionSetter/SetAction",
                payload: {
                    name: "breadcrumbActions",
                    value: (
                        <Link {...styles.printClickable} onClick={openPrintDialog}>
                            {translate("Print")}
                        </Link>
                    ),
                },
            });
        } else {
            dispatch({
                type: "commonActionSetter/SetAction",
                payload: {
                    name: "breadcrumbActions",
                    value: null,
                },
            });
            dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: false } });
        }
    }, [quoteState.value?.properties.isExpired]);

    if (!quote) {
        return null;
    }

    const deleteQuoteClickHandler = () => {
        setDeleteQuoteModalIsOpen(true);
    };

    const submitDeleteQuoteHandler = () => {
        dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: true } });
        deleteQuote({
            quoteId: quote.id,
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Quote deleted."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete() {
                dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: false } });
                // "this" is targeting the object being created, not the parent SFC
                // eslint-disable-next-line react/no-this-in-sfc
                this.onSuccess?.();
            },
        });
    };

    const cancelDeleteQuoteHandler = () => {
        setDeleteQuoteModalIsOpen(false);
    };

    const submitCartNotificationHandler = () => {
        dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: true } });
        if (session.fulfillmentMethod !== quote.fulfillmentMethod) {
            changeCustomerContext({
                ...session,
                fulfillmentMethod: quote.fulfillmentMethod,
                skipRedirect: true,
                onSuccess: _ => {
                    redirectToCheckout(currentLocation);
                },
            });
        } else {
            redirectToCheckout();
        }
    };

    const cancelCartNotificationHandler = () => {
        setCartNotificationModalIsOpen(false);
    };

    const submitQuoteClickHandler = () => {
        dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: true } });
        submitQuote({
            quote,
            onSuccess: () => {
                dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: false } });
                toasterContext.addToast({ body: translate("Quote submitted."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete(resultProps) {
                dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: false } });
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const declineQuoteClickHandler = () => {
        let updatedQuote = { ...quote }; // Create a new object with the properties of 'quote'
        if (!rejectReason) {
            setRejectReasonError(translate("this field is required"));
            return;
        }

        updatedQuote = {
            ...updatedQuote,
            properties: {
                ...updatedQuote.properties,
                rejectReason,
            },
        };

        dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: true } });
        declineQuote({
            quote: updatedQuote,
            onSuccess: () => {
                dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: false } });
                toasterContext.addToast({ body: translate("Quote declined."), messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete(resultProps) {
                dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: false } });
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const acceptQuoteClickHandler = () => {
        if (quote.isJobQuote) {
            acceptJobQuote({
                quote,
                onSuccess: () => {
                    toasterContext.addToast({ body: translate("Quote accepted."), messageType: "success" });
                    redirectToJobQuotes();
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        } else if (cartState.value?.lineCount === 0) {
            if (session.fulfillmentMethod !== quote.fulfillmentMethod) {
                changeCustomerContext({
                    ...session,
                    fulfillmentMethod: quote.fulfillmentMethod,
                    skipRedirect: true,
                    onSuccess: _ => {
                        redirectToCheckout();
                    },
                });
            } else {
                redirectToCheckout();
            }
        } else {
            setCartNotificationModalIsOpen(true);
        }
    };

    const handleConvertToCustomQuote = () => {
        convertToCustomQuote({
            quoteId: quote.id,
            onError: err => {
                toasterContext.addToast({ body: err, messageType: "danger" });
            },
        });
    };

    const handleConvertToProjectList = () => {
        convertToProjectList({
            quoteId: quote.id,
            onError: err => {
                toasterContext.addToast({ body: err, messageType: "danger" });
            },
        });
    };

    const redirectToMyQuotes = () => {
        if (rfqMyQuotesPageUrl) {
            history.push(rfqMyQuotesPageUrl);
        }
    };

    const redirectToCheckout = (defaultUrl = "") => {
        dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: true } });
        if (checkoutUrl) {
            history.push(`${checkoutUrl}?cartId=${quote.id}&isAcceptQuote=true` || defaultUrl);
        }
    };

    const redirectToJobQuotes = () => {
        if (rfqJobQuotesPageUrl) {
            history.push(rfqJobQuotesPageUrl);
        }
    };

    const expirationDateIsGreaterThanCurrentDate = () => {
        if (!quote || !quote.expirationDate) {
            return true;
        }

        const expirationDate = new Date(quote.expirationDate.toString());
        const currentDate = new Date();

        expirationDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        return expirationDate >= currentDate;
    };

    const scrollToSuggested = () => {
        const msgsElement = document.querySelector("#quoteMessages");
        const headerElement = document.querySelector("#header");
        if (msgsElement) {
            const { top } = msgsElement.getBoundingClientRect();
            const headerHeight = headerElement ? headerElement.getBoundingClientRect().height : 0;
            window.scrollTo({
                top: top + window.pageYOffset - headerHeight,
                left: 0,
                behavior: "smooth",
            });
        }
    };

    const quoteType = quote.isJobQuote ? "Job Quote" : "Quote";
    const deleteQuoteLabel = translate(`Delete ${quoteType}`);
    const submitQuoteLabel = translate(`Submit ${quoteType}`);
    const declineQuoteLabel = translate("Decline");
    const acceptQuoteLabel = translate("Accept");
    const canBeSubmitted = quote.isSalesperson && quote.isEditable;
    const expirationDateIsValid = expirationDateIsGreaterThanCurrentDate();
    const canBeDeclined = !quote.isSalesperson && quote.status === "QuoteProposed" && expirationDateIsValid;
    const canBeAccepted =
        !quote.failedToGetRealTimeInventory &&
        !quote.isSalesperson &&
        (quote.status === "QuoteProposed" || quote.status === "AwaitingApproval") &&
        expirationDateIsValid;

    const quoteStatus =
        quote.status === "QuoteProposed" || quote.status === "AwaitingApproval"
            ? { text: translate("Proposed"), type: "info" }
            : quote.status === "QuoteCreated"
              ? { text: "Created", type: "info" }
              : quote.status === "QuoteRequested"
                ? { text: translate("Requested"), type: "info" }
                : quote.status === "QuoteAccepted"
                  ? { text: "Accepted", type: "success" }
                  : { text: "Declined", type: "danger" };

    const canCreateCustomQuote =
        parseBoolean(quote.properties["allowConvertToCustomQuote"]) && !parseBoolean(quote?.properties.isExpired);
    const canConvertToList =
        parseBoolean(quote.properties["allowConvertToList"]) && !parseBoolean(quote?.properties.isExpired);

    const handleRejectReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRejectReason(e.target.value);
        if (e.target.value) {
            setRejectReasonError("");
        }
    };

    return (
        <>
            <StyledWrapper {...styles.headerGridContainer}>
                <StyledWrapper {...styles.titleGridItem}>
                    <Typography {...styles.title}>{`${translate(quoteType)} #${quote.quoteNumber}`}</Typography>
                    <Link {...styles.jumpToMessages} onClick={scrollToSuggested}>
                        {translate("Jump to Messages")}
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.badgeWrapper}>
                    <Typography
                        {...styles.requestedBadge}
                        color={quoteStatus.type}
                        css={css`
                            ${styles.requestedBadge?.css};
                            border-color: ${getColor(quoteStatus.type)};
                        `}
                    >
                        {quoteStatus.text}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.buttonGridItem}>
                    {!isLoading && (
                        <Hidden {...styles.menuHiddenContainer}>
                            <OverflowMenu>
                                {canBeSubmitted && (
                                    <Clickable
                                        {...styles.submitQuoteClickable}
                                        disabled={isQuoteDisabled && isQuoteDisabled.isDisabled}
                                        onClick={submitQuoteClickHandler}
                                    >
                                        {submitQuoteLabel}
                                    </Clickable>
                                )}
                                <Clickable
                                    {...styles.deleteQuoteClickable}
                                    disabled={isQuoteDisabled && isQuoteDisabled.isDisabled}
                                    onClick={deleteQuoteClickHandler}
                                    data-test-selector="rfqQuoteDetails_deleteQuote"
                                >
                                    {deleteQuoteLabel}
                                </Clickable>
                                {canBeAccepted && (
                                    <Clickable
                                        {...styles.acceptQuoteClickable}
                                        disabled={
                                            !quote.erpOrderNumber || (isQuoteDisabled && isQuoteDisabled.isDisabled)
                                        }
                                        onClick={acceptQuoteClickHandler}
                                    >
                                        {acceptQuoteLabel}
                                    </Clickable>
                                )}
                                {canBeDeclined && (
                                    <Clickable
                                        {...styles.declineQuoteClickable}
                                        disabled={isQuoteDisabled && isQuoteDisabled.isDisabled}
                                        onClick={() => setDeclineModalOpen(true)}
                                    >
                                        {declineQuoteLabel}
                                    </Clickable>
                                )}
                                {canCreateCustomQuote && (
                                    <Clickable onClick={handleConvertToCustomQuote}>
                                        {translate("My Customer Quotes")}
                                    </Clickable>
                                )}
                                {canConvertToList && (
                                    <Clickable onClick={handleConvertToProjectList}>
                                        {translate("Create Product List from Quote")}
                                    </Clickable>
                                )}
                            </OverflowMenu>
                        </Hidden>
                    )}
                    {!isLoading && (
                        <Hidden {...styles.buttonsHiddenContainer}>
                            {canBeSubmitted && (
                                <Button
                                    {...styles.submitQuoteButton}
                                    onClick={submitQuoteClickHandler}
                                    disabled={isQuoteDisabled && isQuoteDisabled.isDisabled}
                                    data-test-selector="rfqQuoteDetails_submitQuote"
                                >
                                    {submitQuoteLabel}
                                </Button>
                            )}
                            <Button
                                {...styles.deleteQuoteButton}
                                disabled={isQuoteDisabled && isQuoteDisabled.isDisabled}
                                onClick={deleteQuoteClickHandler}
                                data-test-selector="rfqQuoteDetails_deleteQuote"
                            >
                                {deleteQuoteLabel}
                            </Button>

                            {canBeAccepted && (
                                <Button
                                    {...styles.acceptQuoteButton}
                                    onClick={acceptQuoteClickHandler}
                                    disabled={!quote.erpOrderNumber || (isQuoteDisabled && isQuoteDisabled.isDisabled)}
                                    data-test-selector="rfqQuoteDetails_acceptQuote"
                                >
                                    {acceptQuoteLabel}
                                </Button>
                            )}
                            {canBeDeclined && (
                                <Button
                                    {...styles.declineQuoteButton}
                                    onClick={() => setDeclineModalOpen(true)}
                                    disabled={isQuoteDisabled && isQuoteDisabled.isDisabled}
                                    data-test-selector="rfqQuoteDetails_declineQuote"
                                >
                                    {declineQuoteLabel}
                                </Button>
                            )}
                            {(canCreateCustomQuote || canConvertToList) && (
                                <OverflowMenu
                                    cssOverrides={{
                                        wrapper: css`
                                            margin-left: 10px;
                                        `,
                                        menu: css`
                                            width: auto;
                                            min-width: 100px;
                                        `,
                                    }}
                                >
                                    {canCreateCustomQuote && (
                                        <Clickable onClick={handleConvertToCustomQuote}>
                                            {translate("My Customer Quotes")}
                                        </Clickable>
                                    )}
                                    {canConvertToList && (
                                        <Clickable onClick={handleConvertToProjectList}>
                                            {translate("Create Product List from Quote")}
                                        </Clickable>
                                    )}
                                </OverflowMenu>
                            )}
                        </Hidden>
                    )}
                </StyledWrapper>
                {isQuoteDisabled && isQuoteDisabled.isDisabled && (
                    <StyledWrapper
                        css={css`
                            width: 100%;
                            margin-top: 10px;
                        `}
                    >
                        <Notify
                            messageType="warning"
                            body={
                                <NotificationLink
                                    customerNumber={isQuoteDisabled.currentCustomer}
                                    quoteCustomerNumber={quote.customerNumber}
                                    changeCustomerLink={changeCustomerPageUrl}
                                    currentPageUrl={currentLocation}
                                />
                            }
                        />
                    </StyledWrapper>
                )}
                {quote.isSalesperson && quote.properties.showDifferentWarehouseWarning?.toLowerCase() === "true" && (
                    <StyledWrapper
                        css={css`
                            width: 100%;
                            margin-top: 15px;
                        `}
                    >
                        <Notify
                            messageType="warning"
                            body={siteMessage("Custom_QuoteLinesPricesFromDefaultWarehouse")}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <TwoButtonModal
                {...styles.deleteQuoteModal}
                modalIsOpen={deleteQuoteModalIsOpen}
                headlineText={translate("Delete Quote")}
                messageText={siteMessage("Rfq_DeleteQuoteConfirmation")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={cancelDeleteQuoteHandler}
                onSubmit={submitDeleteQuoteHandler}
                submitTestSelector="submitDeleteQuote"
            />
            <Modal
                isOpen={declineModalIsOpen}
                handleClose={() => setDeclineModalOpen(false)}
                headline={translate("Decline Quote")}
                size={600}
            >
                <Typography>{translate("Are you sure you want to decline the quote?")}</Typography>
                <TextField
                    {...styles.formField}
                    type="text"
                    value={rejectReason}
                    onChange={handleRejectReasonChange}
                    maxLength={120}
                    label={translate("Reject Reason")}
                    placeholder={translate("Please provide a reason for rejecting the quote")}
                    required={true}
                    error={rejectReasonError}
                />
                <StyledWrapper {...styles.modalButtonsWrapper}>
                    <Button
                        onClick={() => setDeclineModalOpen(false)}
                        data-test-selector="rfqQuoteDetails_proceedDeclineQuote"
                        variant="secondary"
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button onClick={declineQuoteClickHandler} data-test-selector="rfqQuoteDetails_proceedDeclineQuote">
                        {translate("Decline")}
                    </Button>
                </StyledWrapper>
            </Modal>
            <TwoButtonModal
                {...styles.cartNotificationModal}
                modalIsOpen={cartNotificationModalIsOpen}
                headlineText={translate("Cart Notification")}
                messageText={siteMessage("Rfq_CartNotificationMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={cancelCartNotificationHandler}
                onSubmit={submitCartNotificationHandler}
                submitTestSelector="submitCartNotification"
            />
        </>
    );
};

const NotificationLink = ({ customerNumber, quoteCustomerNumber, changeCustomerLink, currentPageUrl }: any) => {
    return (
        <span>
            This quote was placed for customer <strong>{quoteCustomerNumber}</strong>, but you are currently logged in
            as <strong>{customerNumber}</strong>. Please visit the&nbsp;
            <a
                style={{ color: "#860038" }}
                href={`${changeCustomerLink}?customerNumber=${quoteCustomerNumber}&returnUrl=${currentPageUrl}`}
            >
                Change Customer
            </a>
            &nbsp;screen to select the correct customer before proceeding.
        </span>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RfqQuoteDetailsHeader)),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["RfqQuoteDetailsPage"],
        fieldDefinitions: [],
        group: "RFQ Quote Details",
    },
};

export default widgetModule;
